import { z } from 'zod';

import { InspectClass } from '@sb/ai/types';
import { inspectCountVariableKey } from '@sb/ai/types/inspectClassKeys';
import { ZeroForceTorqueSensorVariables } from '@sb/integrations/implementations/force-torque-general/steps/ZeroForceTorqueSensor/Variables';
import { ActuateGripperVariables } from '@sb/integrations/implementations/gripper-general/steps/ActuateGripper/Variables';
import { DisableGripperVariables } from '@sb/integrations/implementations/gripper-general/steps/DisableGripper/Variables';
import { EnableGripperVariables } from '@sb/integrations/implementations/gripper-general/steps/EnableGripper/Variables';
import { HaasControlRegionVariables } from '@sb/integrations/implementations/HaasMill/steps/HaasControlRegion/Variables';
import { HaasRunProgramVariables } from '@sb/integrations/implementations/HaasMill/steps/HaasRunProgram/Variables';
import MiraiRunSkillVariables from '@sb/integrations/implementations/MiraiController/steps/MiraiRunSkill/Variables';
import { RotaryWeldVariables } from '@sb/integrations/implementations/WeldMachine/shared/steps/RotaryWeld/Variables';
import { WeldVariables } from '@sb/integrations/implementations/WeldMachine/shared/steps/Variables';
import AntigravityVariables from '@sb/routine-runner/Step/Antigravity/Variables';
import CaptureVariables from '@sb/routine-runner/Step/Capture/Variables';
import ClassifyVariables from '@sb/routine-runner/Step/Classify/Variables';
import CodeBlockVariables from '@sb/routine-runner/Step/CodeBlock/Variables';
import ExternalControlVariables from '@sb/routine-runner/Step/ExternalControl/Variables';
import LocateVariables from '@sb/routine-runner/Step/Locate/Variables';
import LoopVariables from '@sb/routine-runner/Step/Loop/Variables';
import MoveArmToVariables from '@sb/routine-runner/Step/MoveArmTo/Variables';
import MoveArmToV2Variables from '@sb/routine-runner/Step/MoveArmToV2/Variables';
import PressButtonVariables from '@sb/routine-runner/Step/PressButton/Variables';
import RunDemonstrationVariables from '@sb/routine-runner/Step/RunDemonstration/Variables';
import RunInBackgroundVariables from '@sb/routine-runner/Step/RunInBackground/Variables';
import RunLocateSkillVariables from '@sb/routine-runner/Step/RunLocateSkill/Variables';
import RunLearnedModelVariables from '@sb/routine-runner/Step/RunSkill/Variables';
import SamplePositioningApproachVariables from '@sb/routine-runner/Step/SamplePositioningApproach/Variables';
import type { StepKind } from '@sb/routine-runner/Step/StepKind';
import TeleopVariables from '@sb/routine-runner/Step/Teleop/Variables';
import WaypointVariables from '@sb/routine-runner/Step/Waypoint/Variables';

const VARIABLE_SCHEMAS = new Map<string, z.AnyZodObject>([
  ['Antigravity', AntigravityVariables],
  ['Capture', CaptureVariables],
  ['Classify', ClassifyVariables],
  ['CodeBlock', CodeBlockVariables],
  ['ExternalControl', ExternalControlVariables],
  ['Locate', LocateVariables],
  ['Loop', LoopVariables],
  ['MoveArmTo', MoveArmToVariables],
  ['MoveArmToV2', MoveArmToV2Variables],
  ['PressButton', PressButtonVariables],
  ['RunDemonstration', RunDemonstrationVariables],
  ['RunInBackground', RunInBackgroundVariables],
  ['RunLocateSkill', RunLocateSkillVariables],
  ['SamplePositioningApproach', SamplePositioningApproachVariables],
  ['Teleop', TeleopVariables],
  ['Waypoint', WaypointVariables],
  ['RunLearnedModel', RunLearnedModelVariables],
  ['ActuateGripper', ActuateGripperVariables],
  ['EnableGripper', EnableGripperVariables],
  ['DisableGripper', DisableGripperVariables],
  ['ZeroForceTorqueSensor', ZeroForceTorqueSensorVariables],
  ['HaasControlRegion', HaasControlRegionVariables],
  ['HaasRunProgram', HaasRunProgramVariables],
  ['Weld', WeldVariables],
  ['RotaryWeld', RotaryWeldVariables],
  ['MiraiRunSkill', MiraiRunSkillVariables],
] satisfies ReadonlyArray<readonly [StepKind, z.AnyZodObject]>);

const InspectArgs = z.object({
  classes: InspectClass.pick({ id: true }).array().optional(),
});

/** Runtime output fields, including fields the editor does not offer in its menu. */
export function stepOutputNames(
  stepKind: string,
  args: unknown,
): readonly string[] {
  if (stepKind === 'RunInspectSkill') {
    const parsed = InspectArgs.safeParse(args ?? {});

    const counts = parsed.success
      ? (parsed.data.classes ?? []).map(({ id }) => inspectCountVariableKey(id))
      : [];

    return [...counts, ...Object.keys(CaptureVariables.shape)];
  }

  // Only the selected answer is published; the other optional answer fields
  // in the confirmation schema are absent at runtime.
  if (stepKind === 'WaitForConfirmation') {
    const mode = z.object({ mode: z.string() }).safeParse(args);

    switch (mode.success ? mode.data.mode : null) {
      case 'confirm':
        return ['confirmed'];
      case 'choice':
        return ['confirmed', 'choice'];
      case 'freeform':
        return ['confirmed', 'freeformInput'];
      default:
        return [];
    }
  }

  const schema = VARIABLE_SCHEMAS.get(stepKind);

  return schema == null ? [] : Object.keys(schema.shape);
}
